import {
  VARIANCE_ACTIONS,
  VARIANCE_ACTION_LABELS,
  varianceActionExportLane,
} from './varianceActions.js';

const normalizeCode = (value) => String(value || '').trim().toUpperCase();

const firstNonBlank = (...values) => values.find((value) => (
  value !== undefined
  && value !== null
  && String(value).trim() !== ''
)) || '';

const toMoney = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) / 100 : null;
};

const priceFromContext = (context = {}) => toMoney(firstNonBlank(
  context.price,
  context.row?.price,
  context.row?.dealerCost,
  context.row?.dealer_cost,
  context.row?.budget,
  context.row?.mrr,
  context.row?.dealerPrice,
  context.row?.dealer_price,
));

const rowKeyFromContext = (context = {}) => firstNonBlank(
  context.sourceRowKey,
  context.rowKey,
  context.row?.sourceRowKey,
  context.row?.rowKey,
  context.row?.storageKey,
  context.row?.fileRowId,
  context.row?.id,
  context.row?.rowNumber,
);

const evidenceOrderItemId = (context = {}) => (
  context.source === 'cpq'
    ? firstNonBlank(
      context.matchedAppSheetOrderItemId,
      context.row?.matchedAppSheetOrderItemId,
      context.row?.accountMatch?.orderItemId,
    )
    : firstNonBlank(context.orderItemId)
);

const samePrice = (left, right) => {
  if (left === null || right === null) return true;
  return Math.abs(left - right) < 0.005;
};

const compactSource = (context = {}) => ({
  source: context.source,
  sourceLabel: context.sourceLabel,
  productName: context.productName,
  productCode: context.productCode,
  price: priceFromContext(context),
  status: context.status,
  orderItemId: context.orderItemId,
  sourceOrderItemId: context.sourceOrderItemId,
  matchedAppSheetOrderItemId: context.matchedAppSheetOrderItemId,
  referenceId: context.referenceId,
  rowKey: rowKeyFromContext(context),
  row: context.row,
});

export const varianceDropActionKey = (action = {}) => [
  action.actionType,
  action.source || action.sourceFile,
  action.targetSource || action.targetFile,
  action.sourceRowKey,
  action.targetRowKey,
  action.productCode,
  action.replacementProductCode,
  action.proposedPrice,
  action.manualOrderItemId,
  action.manualReferenceId,
].map((value) => String(value ?? '').trim().toLowerCase()).join('|');

export const buildVarianceActionFromEvidenceDrop = ({
  sourceContext,
  targetContext,
  currentUserName,
  now = new Date().toISOString(),
  idFactory = () => `variance-action-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
} = {}) => {
  if (!sourceContext || !targetContext) return null;
  const sourceCode = String(sourceContext.productCode || '').trim();
  const targetCode = String(targetContext.productCode || '').trim();
  const sourcePrice = priceFromContext(sourceContext);
  const targetPrice = priceFromContext(targetContext);
  const manualOrderItemId = String(evidenceOrderItemId(sourceContext) || evidenceOrderItemId(targetContext) || '').trim();
  const manualReferenceId = String(sourceContext.referenceId || targetContext.referenceId || '').trim();
  const targetFile = ['gm', 'cpq'].includes(targetContext.source) ? targetContext.source : null;
  if (targetContext.isFileDrop && targetFile && sourceCode) {
    const actionType = targetFile === 'gm' ? VARIANCE_ACTIONS.ADD_TO_GM : VARIANCE_ACTIONS.ADD_TO_CPQ;
    const sourceSummary = `${sourceContext.productName || sourceCode}${sourceCode ? ` (${sourceCode})` : ''}`;
    return {
      id: idFactory(),
      createdAt: now,
      createdBy: currentUserName,
      actionType,
      actionLabel: VARIANCE_ACTION_LABELS[actionType],
      source: sourceContext.source,
      sourceLabel: sourceContext.sourceLabel,
      targetSource: targetFile,
      targetLabel: targetContext.sourceLabel,
      exportLane: varianceActionExportLane(actionType, sourceContext.source, targetFile),
      productName: sourceContext.productName || sourceCode,
      productCode: sourceCode,
      price: sourcePrice,
      customProductCode: sourceCode,
      customPrice: sourcePrice,
      productStatus: sourceContext.status,
      sourceProductName: sourceContext.productName,
      sourceProductCode: sourceCode,
      sourcePrice,
      manualOrderItemId: String(evidenceOrderItemId(sourceContext) || '').trim(),
      manualReferenceId: String(sourceContext.referenceId || '').trim(),
      sourceRowKey: rowKeyFromContext(sourceContext),
      targetRowKey: `${targetFile}-file-drop`,
      salesforceAccountId: sourceContext.salesforceAccountId || sourceContext.row?.salesforceAccountId || sourceContext.row?.accountId || sourceContext.row?.accountMatch?.salesforceAccountId || '',
      salesforceAccountName: sourceContext.salesforceAccountName || sourceContext.row?.salesforceAccountName || sourceContext.row?.dealerName || sourceContext.row?.accountMatch?.salesforceAccountName || '',
      bindingMethod: sourceContext.bindingMethod || sourceContext.row?.accountMatch?.matchMethod || 'manual_file_drop',
      bindingConfidence: sourceContext.bindingConfidence || sourceContext.row?.accountMatch?.confidence || 100,
      sourceEvidence: compactSource(sourceContext),
      targetEvidence: compactSource(targetContext),
      saveStatus: 'draft',
      problem: `${sourceSummary} is missing from the ${targetContext.sourceLabel || targetFile} file.`,
      fix: `Add ${sourceSummary} to ${targetContext.sourceLabel || targetFile}.`,
      note: '',
      metadata: { createdFrom: 'drag_drop_add_to_file' },
    };
  }
  const codeDiffers = Boolean(sourceCode && targetCode && normalizeCode(sourceCode) !== normalizeCode(targetCode));
  const priceDiffers = !samePrice(sourcePrice, targetPrice);

  if (targetFile && (codeDiffers || priceDiffers)) {
    const actionType = codeDiffers ? VARIANCE_ACTIONS.REPLACE_PRODUCT : VARIANCE_ACTIONS.CHANGE_PRICE;
    const sourceSummary = `${sourceContext.productName || sourceCode || 'source product'}${sourceCode ? ` (${sourceCode})` : ''}`;
    const targetSummary = `${targetContext.productName || targetCode || 'target product'}${targetCode ? ` (${targetCode})` : ''}`;
    const priceSummary = priceDiffers ? ` and price ${targetPrice ?? '--'} -> ${sourcePrice ?? '--'}` : '';
    return {
      id: idFactory(),
      createdAt: now,
      createdBy: currentUserName,
      actionType,
      actionLabel: VARIANCE_ACTION_LABELS[actionType],
      source: targetFile,
      sourceLabel: targetContext.sourceLabel,
      targetSource: targetFile,
      targetLabel: targetContext.sourceLabel,
      exportLane: varianceActionExportLane(actionType, targetFile, targetFile),
      productName: targetContext.productName,
      productCode: targetCode,
      price: targetPrice,
      productStatus: targetContext.status,
      sourceProductName: sourceContext.productName,
      sourceProductCode: sourceCode,
      sourcePrice,
      replacementProductCode: codeDiffers ? sourceCode : '',
      proposedPrice: priceDiffers ? sourcePrice : null,
      manualOrderItemId,
      manualReferenceId,
      sourceRowKey: rowKeyFromContext(targetContext),
      targetRowKey: rowKeyFromContext(sourceContext),
      salesforceAccountId: sourceContext.salesforceAccountId || targetContext.salesforceAccountId || sourceContext.row?.salesforceAccountId || sourceContext.row?.accountId || sourceContext.row?.accountMatch?.salesforceAccountId || '',
      salesforceAccountName: sourceContext.salesforceAccountName || targetContext.salesforceAccountName || sourceContext.row?.salesforceAccountName || sourceContext.row?.dealerName || sourceContext.row?.accountMatch?.salesforceAccountName || '',
      bindingMethod: sourceContext.bindingMethod || sourceContext.row?.accountMatch?.matchMethod || (manualOrderItemId || manualReferenceId ? 'manual_drag_drop' : ''),
      bindingConfidence: sourceContext.bindingConfidence || sourceContext.row?.accountMatch?.confidence || (manualOrderItemId || manualReferenceId ? 100 : null),
      sourceEvidence: compactSource(targetContext),
      targetEvidence: compactSource(sourceContext),
      saveStatus: 'draft',
      problem: `The ${targetContext.sourceLabel || targetFile} row does not match inherited product evidence from ${sourceContext.sourceLabel || sourceContext.source}.`,
      fix: `${actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT ? 'Replace' : 'Adjust'} ${targetSummary} with ${sourceSummary}${priceSummary}.`,
      note: '',
      metadata: { createdFrom: 'drag_drop_product_inheritance' },
    };
  }

  if (!manualOrderItemId && !manualReferenceId) return null;

  const sourceSummary = `${sourceContext.productName || sourceCode || 'source product'}${sourceCode ? ` (${sourceCode})` : ''}`;
  const targetSummary = `${targetContext.productName || targetCode || 'target product'}${targetCode ? ` (${targetCode})` : ''}`;
  return {
    id: idFactory(),
    createdAt: now,
    createdBy: currentUserName,
    actionType: VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH,
    actionLabel: VARIANCE_ACTION_LABELS[VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH],
    source: sourceContext.source,
    sourceLabel: sourceContext.sourceLabel,
    targetSource: targetContext.source,
    targetLabel: targetContext.sourceLabel,
    productName: targetContext.productName,
    productCode: targetCode,
    price: targetPrice,
    productStatus: targetContext.status,
    sourceProductName: sourceContext.productName,
    sourceProductCode: sourceCode,
    sourcePrice,
    manualOrderItemId,
    manualReferenceId,
    sourceRowKey: rowKeyFromContext(sourceContext),
    targetRowKey: rowKeyFromContext(targetContext),
    salesforceAccountId: sourceContext.salesforceAccountId || targetContext.salesforceAccountId || sourceContext.row?.salesforceAccountId || sourceContext.row?.accountId || sourceContext.row?.accountMatch?.salesforceAccountId || '',
    salesforceAccountName: sourceContext.salesforceAccountName || targetContext.salesforceAccountName || sourceContext.row?.salesforceAccountName || sourceContext.row?.dealerName || sourceContext.row?.accountMatch?.salesforceAccountName || '',
    bindingMethod: 'manual_drag_drop',
    bindingConfidence: 100,
    exportLane: 'none',
    problem: 'Automatic product/account evidence matching missed this relationship.',
    fix: `Force ${targetSummary} to inherit evidence from ${sourceSummary}.`,
    note: '',
    sourceEvidence: sourceContext,
    targetEvidence: targetContext,
    saveStatus: 'draft',
  };
};

export const buildVarianceActionFromRemoveDrop = ({
  sourceContext,
  removeTarget,
  currentUserName,
  now = new Date().toISOString(),
  idFactory = () => `variance-action-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
} = {}) => {
  if (!sourceContext || !removeTarget) return null;
  const targetFile = ['gm', 'cpq'].includes(removeTarget.source) ? removeTarget.source : null;
  if (!targetFile || sourceContext.source !== targetFile) return null;
  const sourceCode = String(sourceContext.productCode || '').trim();
  if (!sourceCode) return null;
  const actionType = targetFile === 'gm' ? VARIANCE_ACTIONS.REMOVE_FROM_GM : VARIANCE_ACTIONS.REMOVE_FROM_CPQ;
  const sourcePrice = priceFromContext(sourceContext);
  const sourceSummary = `${sourceContext.productName || sourceCode}${sourceCode ? ` (${sourceCode})` : ''}`;
  const targetLabel = removeTarget.sourceLabel || (targetFile === 'gm' ? 'GM file' : 'CPQ file');
  return {
    id: idFactory(),
    createdAt: now,
    createdBy: currentUserName,
    actionType,
    actionLabel: VARIANCE_ACTION_LABELS[actionType],
    source: targetFile,
    sourceLabel: targetLabel,
    targetSource: targetFile,
    targetLabel,
    exportLane: varianceActionExportLane(actionType, targetFile, targetFile),
    productName: sourceContext.productName || sourceCode,
    productCode: sourceCode,
    price: sourcePrice,
    productStatus: sourceContext.status,
    sourceProductName: sourceContext.productName,
    sourceProductCode: sourceCode,
    sourcePrice,
    manualOrderItemId: String(sourceContext.orderItemId || '').trim(),
    manualReferenceId: String(sourceContext.referenceId || '').trim(),
    sourceRowKey: rowKeyFromContext(sourceContext),
    targetRowKey: rowKeyFromContext(sourceContext),
    salesforceAccountId: sourceContext.salesforceAccountId || sourceContext.row?.salesforceAccountId || sourceContext.row?.accountId || sourceContext.row?.accountMatch?.salesforceAccountId || '',
    salesforceAccountName: sourceContext.salesforceAccountName || sourceContext.row?.salesforceAccountName || sourceContext.row?.dealerName || sourceContext.row?.accountMatch?.salesforceAccountName || '',
    bindingMethod: sourceContext.bindingMethod || sourceContext.row?.accountMatch?.matchMethod || '',
    bindingConfidence: sourceContext.bindingConfidence || sourceContext.row?.accountMatch?.confidence || null,
    sourceEvidence: compactSource(sourceContext),
    targetEvidence: compactSource(sourceContext),
    saveStatus: 'draft',
    problem: `${sourceSummary} should not be billed on the ${targetLabel}.`,
    fix: `Remove ${sourceSummary} from ${targetLabel}.`,
    note: '',
    metadata: { createdFrom: 'drag_drop_remove_from_file' },
  };
};
